import { ActionIcon, Button, Menu, Tooltip } from "@mantine/core";
import { Hand, Maximize, MousePointer2, ZoomIn, ZoomOut } from "lucide-react";
import type { ReactNode } from "react";

import { KeyCaps } from "./ShortcutsModal";
import { zoomLabel, type CanvasView } from "./canvas-view";
import type { Command, KeyCap } from "./keys";
import classes from "./editor.module.css";

/** What the canvas's tools ask for: their keys' own commands (spec capcut-shortcuts.md). */
export type CanvasCommand = Extract<
  Command,
  | "moveTool"
  | "handTool"
  | "zoomToFit"
  | "zoom50"
  | "zoom100"
  | "zoom200"
  | "canvasZoomIn"
  | "canvasZoomOut"
  | "fullScreen"
>;

/** Live's four zoom rows, as its Shortcuts modal's Canvas column writes them, with their keys. */
const ZOOMS: readonly { label: string; keys: readonly KeyCap[]; command: CanvasCommand }[] = [
  { label: "Zoom to fit", keys: ["shift", "F"], command: "zoomToFit" },
  { label: "Zoom to 50%", keys: ["shift", "0"], command: "zoom50" },
  { label: "Zoom to 100%", keys: ["shift", "1"], command: "zoom100" },
  { label: "Zoom to 200%", keys: ["shift", "2"], command: "zoom200" },
];

function ViewTool(props: { label: string; on?: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <Tooltip label={props.label}>
      <ActionIcon
        variant="subtle"
        color="gray"
        size={28}
        aria-label={props.label}
        aria-pressed={props.on}
        data-on={props.on}
        className={classes.switchTool}
        onClick={props.onClick}
      >
        {props.children}
      </ActionIcon>
    </Tooltip>
  );
}

/**
 * The canvas's tools at its bottom right, upright in its side margin (spec capcut-shortcuts.md):
 * Move and the Hand tool, the zoom with live's four rows, and Full screen where the browser has it.
 */
export function CanvasTools(props: {
  view: CanvasView;
  mac: boolean;
  canFullScreen: boolean;
  onCommand: (command: CanvasCommand) => void;
}) {
  const { view, mac, onCommand } = props;
  return (
    <div className={classes.viewTools} role="toolbar" aria-label="Canvas tools">
      <ViewTool label="Move" on={view.tool === "move"} onClick={() => onCommand("moveTool")}>
        <MousePointer2 size={15} />
      </ViewTool>
      <ViewTool label="Hand tool" on={view.tool === "hand"} onClick={() => onCommand("handTool")}>
        <Hand size={15} />
      </ViewTool>
      <span className={classes.toolDivider} aria-hidden />
      <ViewTool label="Zoom in" onClick={() => onCommand("canvasZoomIn")}>
        <ZoomIn size={15} />
      </ViewTool>
      <Menu position="left-end" width={220}>
        <Menu.Target>
          <Button
            variant="subtle"
            color="gray"
            size="compact-sm"
            className={classes.viewZoom}
            aria-label={`Canvas zoom ${zoomLabel(view.zoom)}`}
          >
            {zoomLabel(view.zoom)}
          </Button>
        </Menu.Target>
        <Menu.Dropdown>
          {ZOOMS.map((zoom) => (
            <Menu.Item
              key={zoom.command}
              rightSection={<KeyCaps keys={zoom.keys} mac={mac} plain />}
              onClick={() => onCommand(zoom.command)}
            >
              {zoom.label}
            </Menu.Item>
          ))}
        </Menu.Dropdown>
      </Menu>
      <ViewTool label="Zoom out" onClick={() => onCommand("canvasZoomOut")}>
        <ZoomOut size={15} />
      </ViewTool>
      {props.canFullScreen ? (
        <>
          <span className={classes.toolDivider} aria-hidden />
          <ViewTool label="Full screen" onClick={() => onCommand("fullScreen")}>
            <Maximize size={15} />
          </ViewTool>
        </>
      ) : null}
    </div>
  );
}
